import { addDays, format, startOfWeek } from 'date-fns'
import { useMemo } from 'react'
import { dateAdjustedForTimezone } from '@opallabs/lambda'
import { contentHasScheduledTime } from '../WeekView/momentTypes'
import type { PartialContent, PartialMoment } from '../WeekView/momentTypes'
import { useWorkspaceServiceIconMap } from '../WeekView/queries/useSystemServices'
import { formatHourId, formatHourLabel } from '../WeekView/sortHelpers'
import { WeekViewSortType } from '../WeekView/types'
import type { WeekViewSort } from '../WeekView/types'

export type BreakdownParentGroup = { id: string; name: string }
export type BreakdownGroupMeta = {
  name: string
  color: string
  parentGroup?: BreakdownParentGroup
}
export type BreakdownByDate = Record<string, Record<string, number>>

type ContentMap = Record<string, PartialContent | undefined>
type MomentMap = Record<string, PartialMoment | undefined>

const FALLBACK_COLOR = '#6B7280'

// Catch-all bucket for content with no value for the active dimension (e.g.
// no account in Account sort). Mirrors the grid's "No Associated …" row so
// the breakdown rows always sum to the tooltip total.
const NO_GROUP_ID = '__no_group__'

// Sorts whose breakdown maps cleanly to a stacked-segment bar; other sorts
// only feed the tooltip. Hoisted so the array isn't allocated per render.
const STACKING_SORTS: readonly WeekViewSortType[] = [
  WeekViewSortType.Channel,
  WeekViewSortType.Account,
  WeekViewSortType.LabelSet,
]

// Label `color` arrives bare-hex from the API (`a52a2a`), which is
// invalid as a CSS color. Mirrors `useCardStripeColor`'s normalization.
const ensureHashPrefix = (color: string | null | undefined): string | null => {
  if (!color) return null
  return color.startsWith('#') ? color : `#${color}`
}

type ServiceInfoMap = Record<
  string,
  { name?: string; color?: string } | undefined
>

type BreakdownAcc = {
  breakdownByDate: BreakdownByDate
  groupMeta: Record<string, BreakdownGroupMeta>
}

type ReduceCtx = {
  dayKey: string
  serviceInfoMap: ServiceInfoMap | undefined
  moments: MomentMap | undefined
  targetLabelSetId: string
  noGroupLabel: string
}

const recordNoGroup = (acc: BreakdownAcc, ctx: ReduceCtx): BreakdownAcc =>
  recordLeaf(acc, ctx.dayKey, NO_GROUP_ID, {
    name: ctx.noGroupLabel,
    color: FALLBACK_COLOR,
  })

const recordLeaf = (
  acc: BreakdownAcc,
  dayKey: string,
  groupId: string,
  meta: BreakdownGroupMeta
): BreakdownAcc => {
  acc.breakdownByDate[dayKey][groupId] =
    (acc.breakdownByDate[dayKey][groupId] ?? 0) + 1
  acc.groupMeta[groupId] ??= meta
  return acc
}

const reduceByChannel = (
  acc: BreakdownAcc,
  item: PartialContent,
  ctx: ReduceCtx
): BreakdownAcc => {
  if (!item.service?.id) return recordNoGroup(acc, ctx)
  const id = String(item.service.id)
  const info = ctx.serviceInfoMap?.[id]
  return recordLeaf(acc, ctx.dayKey, id, {
    name: info?.name ?? item.service.system_name ?? 'Unknown',
    color: info?.color ?? FALLBACK_COLOR,
  })
}

const reduceByAccount = (
  acc: BreakdownAcc,
  item: PartialContent,
  ctx: ReduceCtx
): BreakdownAcc => {
  if (!item.account?.id) return recordNoGroup(acc, ctx)
  const accountId = String(item.account.id)
  const serviceId = item.service?.id ? String(item.service.id) : null
  const serviceInfo = serviceId ? ctx.serviceInfoMap?.[serviceId] : null
  const serviceName =
    serviceInfo?.name ?? item.service?.system_name ?? 'Unknown'
  const serviceColor = serviceInfo?.color ?? FALLBACK_COLOR
  recordLeaf(acc, ctx.dayKey, accountId, {
    name: item.account.name ?? 'Unknown',
    color: serviceColor,
    parentGroup: serviceId ? { id: serviceId, name: serviceName } : undefined,
  })
  // Stash parent meta so the bar-aggregation pass below can paint the
  // service color even when no content under this Account row references
  // the service directly.
  if (serviceId) {
    acc.groupMeta[serviceId] ??= { name: serviceName, color: serviceColor }
  }
  return acc
}

type BreakdownLabel = NonNullable<PartialContent['labels']>[number]

const reduceByLabelSet = (
  acc: BreakdownAcc,
  item: PartialContent,
  ctx: ReduceCtx
): BreakdownAcc => {
  // Match the same id-based logic as `useCardStripeColor` — names
  // aren't unique across workspaces in edge cases.
  const inTargetSet = (label: BreakdownLabel): boolean =>
    String(label.label_set?.id) === ctx.targetLabelSetId

  // A post can carry several labels from the sorted set, and the grid renders
  // it in every matching row — so count it in each one. Labels like "Pillar"
  // usually sit at the moment level, so fall back to the parent moment's
  // labels, mirroring `getContentLabels`.
  let labels: BreakdownLabel[] = (item.labels ?? []).filter(inTargetSet)
  if (labels.length === 0 && ctx.moments) {
    const parentMomentId = item.relationships?.moment?.id
    const parentMoment = parentMomentId
      ? ctx.moments[parentMomentId]
      : undefined
    labels = (parentMoment?.labels ?? []).filter(inTargetSet)
  }

  // Count distinct ids: the moments endpoint repeats the same label several
  // times per moment, and a repeat is not a second post.
  const countedIds = new Set<string>()
  labels.forEach((label) => {
    if (!label.id) return
    const groupId = String(label.id)
    if (countedIds.has(groupId)) return
    countedIds.add(groupId)
    recordLeaf(acc, ctx.dayKey, groupId, {
      name: label.name ?? 'Unknown',
      color: ensureHashPrefix(label.color) ?? FALLBACK_COLOR,
    })
  })

  return countedIds.size > 0 ? acc : recordNoGroup(acc, ctx)
}

const reduceByTime = (
  acc: BreakdownAcc,
  item: PartialContent,
  ctx: ReduceCtx
): BreakdownAcc => {
  if (typeof item.scheduled_at !== 'string') return acc
  // No chosen time — group under "Unscheduled" like the grid does, not the
  // midnight hour the fallback `scheduled_at` would suggest.
  if (!contentHasScheduledTime(item)) return recordNoGroup(acc, ctx)
  const adjusted = dateAdjustedForTimezone(item.scheduled_at)
  if (!adjusted) return acc
  const hour = adjusted.getHours()
  return recordLeaf(acc, ctx.dayKey, formatHourId(hour), {
    name: formatHourLabel(hour),
    color: FALLBACK_COLOR,
  })
}

type ReduceFn = (
  acc: BreakdownAcc,
  item: PartialContent,
  ctx: ReduceCtx
) => BreakdownAcc

const REDUCERS: Partial<Record<WeekViewSortType, ReduceFn>> = {
  [WeekViewSortType.Channel]: reduceByChannel,
  [WeekViewSortType.Account]: reduceByAccount,
  [WeekViewSortType.LabelSet]: reduceByLabelSet,
  [WeekViewSortType.Time]: reduceByTime,
}

// Account is the only sort where the bar segments aggregate differently
// from the tooltip leaves (parent service vs leaf account). Returns a
// fresh map; other sorts just reuse `breakdownByDate`.
const aggregateAccountBars = (
  breakdownByDate: BreakdownByDate,
  groupMeta: Record<string, BreakdownGroupMeta>
): BreakdownByDate => {
  const aggregated: BreakdownByDate = {}
  for (const [dayKey, leafCounts] of Object.entries(breakdownByDate)) {
    const bar: Record<string, number> = {}
    for (const [accountId, count] of Object.entries(leafCounts)) {
      const parent = groupMeta[accountId]?.parentGroup
      const barKey = parent?.id ?? accountId
      bar[barKey] = (bar[barKey] ?? 0) + count
    }
    aggregated[dayKey] = bar
  }
  return aggregated
}

type UseBreakdownArgs = {
  content: ContentMap | undefined
  moments?: MomentMap | undefined
  focusedDay: Date
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6
  selectedSort: WeekViewSort
  currentBrandId?: string | number
  spanInMarketDays?: boolean
}

export type UseBreakdownResult = {
  /** Leaf-level breakdown — drives the tooltip. */
  breakdownByDate: BreakdownByDate
  /**
   * Bar-level breakdown — drives the stacked segments. Equal to
   * `breakdownByDate` for non-grouped sorts. For Account sort, this is
   * aggregated by parent service so the bar visually matches Channel sort.
   */
  barBreakdownByDate: BreakdownByDate
  /**
   * Focused-week tooltip rows: each piece of content overlapping the week
   * counted once by group (including content carried in from a prior week in
   * span mode), so the rows reconcile with the distinct-overlap week total.
   */
  weekBreakdown: Record<string, number>
  colorFor: (groupId: string) => string
  nameFor: (groupId: string) => string
  /** Returns a parent group (e.g. service for Account sort) or null. */
  parentOf: (groupId: string) => BreakdownParentGroup | null
  /** False for sorts that don't stack the bar (Time, None) — tooltip-only. */
  shouldStackSegments: boolean
  /** True only when the active sort can produce a meaningful breakdown. */
  hasBreakdownDimension: boolean
}

/**
 * Histogram breakdown for the active week's 7 days, grouped by the
 * current sort. Account sort stacks bars by parent service (matching
 * Channel sort visually) while the tooltip exposes the account mix
 * under each service. Returns an empty breakdown for `None` sort or
 * when content isn't loaded.
 */
export const useBreakdown = ({
  content,
  moments,
  focusedDay,
  weekStartsOn,
  selectedSort,
  currentBrandId,
  spanInMarketDays,
}: UseBreakdownArgs): UseBreakdownResult => {
  const { data: serviceInfoMap } = useWorkspaceServiceIconMap(
    currentBrandId == null ? '' : String(currentBrandId)
  )

  return useMemo(() => {
    const sortType = selectedSort.type
    const targetLabelSetId = String(selectedSort.id)
    const reducer = REDUCERS[sortType]

    const seededBreakdown: BreakdownByDate = {}
    const firstDay = startOfWeek(focusedDay, { weekStartsOn })
    const weekDayKeys: string[] = []
    const dayKeys = new Set<string>()
    for (let i = 0; i < 7; i += 1) {
      const key = format(addDays(firstDay, i), 'yyyy-MM-dd')
      weekDayKeys.push(key)
      dayKeys.add(key)
      // Seed every active-week day so downstream consumers can detect
      // "in active week" via map presence regardless of content count.
      seededBreakdown[key] = {}
    }

    // Label for the catch-all row, matching the grid's "No Associated …".
    const noGroupLabel =
      sortType === WeekViewSortType.Channel
        ? 'No Associated Channel'
        : sortType === WeekViewSortType.Account
          ? 'No Associated Account'
          : sortType === WeekViewSortType.LabelSet
            ? `No Associated ${selectedSort.title}`
            : 'Unscheduled'

    const empty: UseBreakdownResult = {
      breakdownByDate: seededBreakdown,
      barBreakdownByDate: seededBreakdown,
      weekBreakdown: {},
      colorFor: () => FALLBACK_COLOR,
      nameFor: () => 'Unknown',
      parentOf: () => null,
      shouldStackSegments: false,
      hasBreakdownDimension: false,
    }

    if (!content || !reducer) return empty

    // Days within the focused week a piece of content occupies: its full
    // in-market range in span mode, otherwise just its scheduled day — so the
    // day breakdown (and the bar segments) stay aligned with the in-market
    // bars rather than only marking the launch day.
    const inMarketWeekDayKeys = (item: PartialContent): string[] => {
      if (typeof item.scheduled_at !== 'string') return []
      const start = dateAdjustedForTimezone(item.scheduled_at)
      if (!start) return []
      const startKey = format(start, 'yyyy-MM-dd')
      if (!spanInMarketDays || typeof item.takedown_scheduled_at !== 'string') {
        return dayKeys.has(startKey) ? [startKey] : []
      }
      const end = dateAdjustedForTimezone(item.takedown_scheduled_at) ?? start
      const endKey = format(end, 'yyyy-MM-dd')
      return weekDayKeys.filter((d) => d >= startKey && d <= endKey)
    }

    const { breakdownByDate, groupMeta } = Object.values(
      content
    ).reduce<BreakdownAcc>(
      (acc, item) => {
        if (!item) return acc
        inMarketWeekDayKeys(item).forEach((dayKey) =>
          reducer(acc, item, {
            dayKey,
            serviceInfoMap,
            moments,
            targetLabelSetId,
            noGroupLabel,
          })
        )
        return acc
      },
      { breakdownByDate: seededBreakdown, groupMeta: {} }
    )

    const barBreakdownByDate =
      sortType === WeekViewSortType.Account
        ? aggregateAccountBars(breakdownByDate, groupMeta)
        : breakdownByDate

    // Week-level rows count each piece of content overlapping the focused week
    // once (by group), so they reconcile with the distinct-overlap week total.
    // `breakdownByDate` alone would miss content launched in a prior week that
    // merely spans into this one. In span mode a post occupies its full
    // in-market range; otherwise only its scheduled day. Reuses the same
    // reducer + `groupMeta` via a synthetic single bucket.
    const WEEK_BUCKET = '__week__'
    const weekStartKey = format(firstDay, 'yyyy-MM-dd')
    const weekEndKey = format(addDays(firstDay, 6), 'yyyy-MM-dd')
    const weekAcc: BreakdownAcc = {
      breakdownByDate: { [WEEK_BUCKET]: {} },
      groupMeta,
    }
    Object.values(content).forEach((item) => {
      if (!item || typeof item.scheduled_at !== 'string') return
      const start = dateAdjustedForTimezone(item.scheduled_at)
      if (!start) return
      const end =
        spanInMarketDays && typeof item.takedown_scheduled_at === 'string'
          ? (dateAdjustedForTimezone(item.takedown_scheduled_at) ?? start)
          : start
      const startKey = format(start, 'yyyy-MM-dd')
      const endKey = format(end, 'yyyy-MM-dd')
      if (startKey > weekEndKey || endKey < weekStartKey) return
      reducer(weekAcc, item, {
        dayKey: WEEK_BUCKET,
        serviceInfoMap,
        moments,
        targetLabelSetId,
        noGroupLabel,
      })
    })

    return {
      breakdownByDate,
      barBreakdownByDate,
      weekBreakdown: weekAcc.breakdownByDate[WEEK_BUCKET],
      colorFor: (groupId) => groupMeta[groupId]?.color ?? FALLBACK_COLOR,
      nameFor: (groupId) => groupMeta[groupId]?.name ?? 'Unknown',
      parentOf: (groupId) => groupMeta[groupId]?.parentGroup ?? null,
      shouldStackSegments: STACKING_SORTS.includes(sortType),
      hasBreakdownDimension: true,
    }
  }, [
    content,
    moments,
    focusedDay,
    weekStartsOn,
    selectedSort,
    serviceInfoMap,
    spanInMarketDays,
  ])
}
